// ============================================================
// MASH machine_card viewer — section bodies + roll-up (ME-MACHINEID-RENDER SP-2)
// ============================================================
// Pure presentational composition of the SP-1a honesty atoms (FigureTile /
// VerdictChip / CoverageValue / Breakdown / CameraWatchBanner). NO honesty logic
// of its own — it consumes the SP-1a engine resolutions AS-IS (esp. the
// freshness-authority rule, §7A flag #1: freshness comes from the card-level
// FreshnessBanner in the parent; nothing here recomputes it from age).
import React from "react";

import type {
  EvidenceTab,
  Figure,
  MachineCardArtifactData,
  WindowKey,
} from "../../../../../shared/components/machineEvidence";
import {
  Breakdown,
  CameraWatchBanner,
  CoverageValue,
  FigureTile,
  VerdictChip,
  WINDOW_KEYS,
  deriveCameraWatch,
  isPlaceholderTab,
  projectFigureToWindow,
  resolveBreakdown,
  resolveCoverage,
  resolveVerdict,
} from "../../../../../shared/components/machineEvidence";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { dt } from "../../../../../shared/themes/redesignTokens";
import {
  ARM_ALSO_MEASURED_KEYS,
  TRAY_UNUSED_SLOTS,
  WINDOW_PILL_LABELS,
  orderedSectionTabs,
  tabLabel,
  worstVerdictTab,
} from "./machineCardConstants";

/** Translate a tab's display label: client six-tab map by slug, falling back to
 *  the tab's own (payload) name for a stray/unknown slug — payload names are
 *  NEVER translated. */
export const useTabLabel = () => {
  const { translate } = useTranslation("machineEvidence");
  return (tab: EvidenceTab): string =>
    translate(`tab.${tab.part_slug}`, { defaultValue: tabLabel(tab) }) as string;
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: 12,
};

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  color: dt.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontFamily: dt.fontMono,
};

// ───────────────────────────────────────────────────────────────────
// Period-pill row — FUNCTIONAL card-level window selector (SP-1b; §7 Q2). The
// five pills render in FIXED order (WINDOW_KEYS). Selecting a window re-reads
// every WINDOWED figure's matching `windows[]` entry (value/count/coverage) —
// purely client-side over the delivered payload, NO refetch. An `absent` window
// shows "not available" (never a scaled number). Snapshot figures ignore it. The
// parent HIDES this row entirely when a card has NO windowed figures.
// ───────────────────────────────────────────────────────────────────
export const PeriodPillRow: React.FC<{
  selected: WindowKey;
  onSelect: (key: WindowKey) => void;
}> = ({ selected, onSelect }) => {
  const { translate } = useTranslation("machineEvidence");
  return (
    <div
      className="msc-period-pills"
      role="tablist"
      aria-label="Period window"
      style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}
    >
      {WINDOW_KEYS.map((key) => {
        const active = key === selected;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            data-window-key={key}
            onClick={() => onSelect(key)}
            style={{
              padding: "2px 10px",
              borderRadius: dt.radiusPill,
              fontFamily: dt.fontMono,
              fontSize: 11,
              fontWeight: active ? 600 : 500,
              color: active ? dt.textPrimary : dt.textSecondary,
              background: active ? dt.blueSoft : dt.inputBg,
              border: `1px solid ${active ? dt.cardBorder : "transparent"}`,
              cursor: "pointer",
            }}
          >
            {translate(`window.${key}`, { defaultValue: WINDOW_PILL_LABELS[key] }) as string}
          </button>
        );
      })}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Provisional tray visual — 6-of-8 fallback (GAP-2 / §7 Q3). Muted cells are
// the two known-unused slots; flagged PROVISIONAL until the bot ships the
// authoritative physical position map.
// ───────────────────────────────────────────────────────────────────
export const TrayVisual: React.FC = () => {
  const { translate } = useTranslation("machineEvidence");
  const cells = Array.from({ length: 8 }, (_, i) => `${Math.floor(i / 4) + 1}_${(i % 4) + 1}`);
  return (
    <div className="msc-tray" data-provisional="tray-6of8" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={eyebrow}>
        {translate("tray.title", { defaultValue: "Tray fill (provisional)" }) as string}
      </span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 28px)", gap: 6 }}>
        {cells.map((slot) => {
          const unused = TRAY_UNUSED_SLOTS.includes(slot);
          return (
            <div
              key={slot}
              title={unused ? (translate("tray.slotUnused", { slot, defaultValue: "{{slot}} — unused" }) as string) : slot}
              style={{
                width: 28,
                height: 28,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                background: unused ? dt.inputBg : dt.roleGreenBg,
                opacity: unused ? 0.45 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: dt.fontMono,
                fontSize: 9,
                color: unused ? dt.textMuted : dt.roleGreen,
              }}
            >
              {slot}
            </div>
          );
        })}
      </div>
      <span style={{ fontSize: 11, color: dt.textMuted, fontStyle: "italic" }}>
        {translate("tray.provisionalCaption", {
          defaultValue: "provisional 6-of-8 fallback — awaiting bot physical position map (GAP-2)",
        }) as string}
      </span>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Hero figure — the headline number for a section, rendered large. Reuses the
// coverage engine + Breakdown atom (per-bowl / per-stream bars).
// ───────────────────────────────────────────────────────────────────
const Hero: React.FC<{ figure: Figure; dim: boolean }> = ({ figure, dim }) => {
  const coverage = resolveCoverage(figure);
  const breakdown = resolveBreakdown(figure.breakdown);
  return (
    <div className="msc-hero" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 12, color: dt.textSecondary }}>{figure.label}</span>
      <div style={{ fontSize: 30, lineHeight: 1.1 }}>
        <CoverageValue resolution={coverage} unit={figure.unit} denominator={figure.denominator} dim={dim} />
      </div>
      {breakdown ? <Breakdown resolution={breakdown} /> : null}
    </div>
  );
};

const FiguresGrid: React.FC<{
  figures: Figure[];
  card: MachineCardArtifactData;
  selectedWindow: WindowKey;
}> = ({ figures, card, selectedWindow }) => {
  if (figures.length === 0) return null;
  return (
    <div style={gridStyle}>
      {figures.map((f) => (
        <FigureTile
          key={f.id}
          figure={projectFigureToWindow(f, selectedWindow)}
          cardFreshness={card.freshness}
          showBreakdown
        />
      ))}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Arm — Picker + Jaws MERGED into ONE tab, rendered as TWO GROUPS (PICKING +
// SIZE). SIZE = the residual-band figure (the ONLY figure carrying a
// `threshold`, plus any jaw/residual/size id) — everything else is PICKING.
// GAP-6 "also measured, not shown yet" slot = signal-key labels only.
// ───────────────────────────────────────────────────────────────────
const isSizeFigure = (f: Figure): boolean =>
  f.threshold != null || /jaw|residual|size/i.test(`${f.id} ${f.label}`);

export const ArmGroups: React.FC<{
  tab: EvidenceTab;
  card: MachineCardArtifactData;
  selectedWindow: WindowKey;
}> = ({ tab, card, selectedWindow }) => {
  const { translate } = useTranslation("machineEvidence");
  const figures = tab.figures ?? [];
  const sizeFigs = figures.filter(isSizeFigure);
  const pickingFigs = figures.filter((f) => !isSizeFigure(f));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={eyebrow}>{translate("arm.picking", { defaultValue: "Picking" }) as string}</span>
        {pickingFigs.length > 0 ? (
          <FiguresGrid figures={pickingFigs} card={card} selectedWindow={selectedWindow} />
        ) : (
          <span style={{ fontSize: 12, color: dt.textMuted, fontStyle: "italic" }}>
            {translate("arm.noPickingFigures", { defaultValue: "— no picking figures this window" }) as string}
          </span>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={eyebrow}>{translate("arm.size", { defaultValue: "Size" }) as string}</span>
        {sizeFigs.length > 0 ? (
          <FiguresGrid figures={sizeFigs} card={card} selectedWindow={selectedWindow} />
        ) : (
          <span style={{ fontSize: 12, color: dt.textMuted, fontStyle: "italic" }}>
            {translate("arm.noSizeFigures", { defaultValue: "— no size figures this window" }) as string}
          </span>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={eyebrow}>
            {translate("arm.alsoMeasured", { defaultValue: "Also measured, not shown yet" }) as string}
          </span>
          <span style={{ fontFamily: dt.fontMono, fontSize: 11, color: dt.textMuted }}>
            {ARM_ALSO_MEASURED_KEYS.join(" · ")}
          </span>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Section body — verdict + hero + supporting tiles + section-specific chrome.
// Placeholder tabs render "Pending — not wired this milestone", NEVER a 0/fault.
// ───────────────────────────────────────────────────────────────────
export const SectionBody: React.FC<{
  tab: EvidenceTab;
  card: MachineCardArtifactData;
  dim: boolean;
  selectedWindow: WindowKey;
}> = ({ tab, card, dim, selectedWindow }) => {
  const { translate } = useTranslation("machineEvidence");
  const verdict = resolveVerdict(tab.verdict);
  const figures = tab.figures ?? [];
  const [hero, ...rest] = figures;

  if (isPlaceholderTab(tab)) {
    return (
      <div className="msc-section-body msc-section-placeholder" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <VerdictChip resolution={verdict} chipOnly />
        <span data-placeholder style={{ fontSize: 13, color: dt.textMuted, fontStyle: "italic" }}>
          {/* Payload headline renders as-is; only the client FALLBACK is translated. */}
          {verdict.headlineIsFallback
            ? (translate("section.pendingPlaceholder", {
                defaultValue: "Pending — not wired this milestone",
              }) as string)
            : verdict.headline}
        </span>
      </div>
    );
  }

  return (
    <div className="msc-section-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <VerdictChip resolution={verdict} />
      {tab.part_slug === "arm" ? (
        <ArmGroups tab={tab} card={card} selectedWindow={selectedWindow} />
      ) : (
        <>
          {hero ? <Hero figure={projectFigureToWindow(hero, selectedWindow)} dim={dim} /> : null}
          <FiguresGrid figures={rest} card={card} selectedWindow={selectedWindow} />
        </>
      )}
      {tab.part_slug === "unloader" ? <TrayVisual /> : null}
      {tab.part_slug === "vision" ? <CameraWatchBanner show={deriveCameraWatch(tab, card)} /> : null}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Overview roll-up header — worst verdict across POPULATED sections + a compact
// per-section headline grid. The promoted exception banner is rendered by the
// parent viewer ABOVE this. Placeholder sections show "Pending", never 0/fault.
// ───────────────────────────────────────────────────────────────────
export const OverviewRollup: React.FC<{
  card: MachineCardArtifactData;
  onSelect: (slug: string) => void;
  selectedWindow: WindowKey;
}> = ({ card, onSelect, selectedWindow }) => {
  const { translate } = useTranslation("machineEvidence");
  const translateTabLabel = useTabLabel();
  const worst = worstVerdictTab(card);
  const worstVerdict = worst ? resolveVerdict(worst.verdict) : resolveVerdict(null);
  return (
    <div
      className="msc-overview-rollup"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 12,
        borderRadius: dt.radiusSm,
        background: dt.headerBg,
        border: `1px solid ${dt.cardBorder}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={eyebrow}>{translate("overview.worstVerdict", { defaultValue: "Worst verdict" }) as string}</span>
        <VerdictChip resolution={worstVerdict} />
        {worst ? <span style={{ fontSize: 12, color: dt.textSecondary }}>· {translateTabLabel(worst)}</span> : null}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
        {orderedSectionTabs(card).map((tab) => {
          const placeholder = isPlaceholderTab(tab);
          const rawHero = (tab.figures ?? [])[0];
          const hero = rawHero ? projectFigureToWindow(rawHero, selectedWindow) : null;
          const coverage = hero ? resolveCoverage(hero) : null;
          return (
            <button
              key={tab.part_slug}
              type="button"
              onClick={() => onSelect(tab.part_slug)}
              style={{
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                gap: 4,
                padding: 8,
                borderRadius: dt.radiusSm,
                background: dt.cardBg,
                border: `1px solid ${dt.cardBorder}`,
                cursor: "pointer",
                fontFamily: dt.font,
              }}
            >
              <span style={{ fontSize: 12, color: dt.textSecondary, fontWeight: 600 }}>{translateTabLabel(tab)}</span>
              {placeholder ? (
                <span data-placeholder style={{ fontSize: 12, color: dt.textMuted, fontStyle: "italic" }}>
                  {translate("overview.pending", { defaultValue: "Pending" }) as string}
                </span>
              ) : coverage ? (
                <span style={{ fontSize: 15 }}>
                  <CoverageValue resolution={coverage} unit={hero?.unit} denominator={hero?.denominator} />
                </span>
              ) : (
                <span data-placeholder style={{ fontSize: 12, color: dt.textMuted, fontStyle: "italic" }}>
                  {translate("overview.pending", { defaultValue: "Pending" }) as string}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
